import cookie from 'js-cookie';
import type { PageType } from 'types';
import type { User } from 'types/User';

import {
  getPageSeq,
  getPageViewId,
  getPreviousPageType,
  getPreviousPageViewId
} from '~/utils/navigationContext';
import { parseSearchString } from '~/utils/qs';

import UserEvent from './UserEvent';

type EventData<T> = {
  clientIp: string | null;
  isBot: boolean;
  user: User;
  viewProperties?: T;
  viewType: PageType;
};

const SCHEMA_VERSION = 1;

// Search PLPs are emitted as view_type 'plp', so collapse the legacy 'search'
// page type wherever a view type appears on the core view payload.
function normalizeViewType(pageType: PageType): PageType {
  return pageType === 'search' ? 'plp' : pageType;
}

class ViewEvent<
  T extends Record<string, unknown> = Record<string, unknown>
> extends UserEvent<EventData<T>> {
  // Shared across instances so a re-render that re-fires the same view_id is
  // deduped; keyed on view_id so each new navigation still sends once.
  private static lastSentId?: string;

  private getUtmParams() {
    const params = parseSearchString(window.location.search);

    return {
      utm_campaign: params.utm_campaign || undefined,
      utm_content: params.utm_content || undefined,
      utm_medium: params.utm_medium || undefined,
      utm_source: params.utm_source || undefined,
      utm_term: params.utm_term || undefined
    };
  }

  collectServerEvents() {
    const viewId = getPageViewId();

    if (!viewId || ViewEvent.lastSentId === viewId) return;

    ViewEvent.lastSentId = viewId;

    const previousViewId = getPreviousPageViewId();
    const previousPageType = getPreviousPageType();

    this.serverEvents.push({
      data: {
        ...this.getUtmParams(),
        event_name: 'view',
        guest_id: this.data.user.guestId ?? cookie.get('clickRefUid'),
        platform_properties: {
          client_ip: this.data.clientIp ?? undefined,
          page_path: window.location.pathname,
          referrer: document.referrer || undefined,
          user_agent: navigator.userAgent
        },
        previous_view_id: previousViewId,
        previous_view_type:
          previousViewId && previousPageType
            ? normalizeViewType(previousPageType)
            : undefined,
        schema_version: SCHEMA_VERSION,
        view_id: viewId,
        view_properties: this.data.viewProperties,
        view_seq: getPageSeq(),
        view_type: normalizeViewType(this.data.viewType)
      },
      type: 'view'
    });
  }
}

export default ViewEvent;
